import {autobind} from 'core-decorators';
import type debug from 'debug';

import {Component} from 'inferno';

import {Metadata} from '../metadata';
import rootScope from '../services/rootScope';
import {appDebug, cleanTermsForURL, document, encodeHeaderVal, fullURL, testLogging, localURL, removeEmptyStrings, window} from '../util';
import emitter from '../util/emitter';

export {NotImplementedError} from './NotImplementedError';
export {Copybox} from './Copybox';
export {Link} from './Link';
export {Button, InputButton} from './buttons';
export {ShareIcon} from './ShareIcon';
export {Tooltip, TooltipWrapper} from './Tooltip';

import './index.scss';

const componentsDebug = appDebug.extend('components');


export interface CustomComponentProps extends jsx.Props {
}

export interface CustomComponentState extends jsx.State{
    updateCounter: number;
}

export class CustomComponent<
    ChildProps extends CustomComponentProps = CustomComponentProps,
    ChildState extends CustomComponentState = CustomComponentState
> extends Component<ChildProps, ChildState> {
    static debugNamespace = 'CustomComponent';

    constructor(props, context: jsx.AppGlobals) {
        super(props, context);
        this.state = {
            ...this.state,
            updateCounter: 0,
        };
    }
    extendDebug(namespace: string): debug.Debugger {
        return this.context.request.console.extendDebug(
            componentsDebug.extend(
                this.constructor['debugNamespace'] || ''),
            namespace,
        );
    }
    debug(namespace: string, formatter?: any, ...message: any[]) {
        this.extendDebug(namespace)(formatter || null, ...message);
    }
    setStateSynchronously(newState: jsx.State) {
        // FIXME: we shouldn't be doing this, better to replce with async
        // setState() calls.
        Object.assign(this.state, newState);
    }
    setState(newState, callback?: (() => void)) {
        if (!this.$BS) {
            super.setState(newState, callback);
        } else {
            Object.assign(this.state, newState);
            callback?.();
        }
    }
    @autobind
    triggerUpdate() {
        // Only needed if already in middle of update and DOM exists.
        if (!this.$BS) {
            // If a child component overwrite this.state, this param can get
            // lost.
            let {updateCounter} = this.state;
            if (typeof updateCounter === 'undefined') {
                this.context.request.console.warn(
                    'this.state.updateCounter is undefined');
                updateCounter = 0;
            }
            updateCounter += 1;
            this.setState({updateCounter});
        }
    }
    fullURL(url: string): string {
        return fullURL(this.context.CONSTANTS, url);
    }
    redirect(url: string, permanent?: boolean) {
        const shouldWarnUnlocalizedUrl =
            this.context.gtService.shouldWarnUnlocalizedUrl;
        // We need to encode here, not in ssr-common.js, so that it doesn't
        // throw an error when added to add to the Headers object when
        // non-ASCII characters are present.
        url = encodeURI(url);
        this.debug('redirect', url, permanent);
        this.context.response.status = permanent ? 301 : 302;
        this.context.response.headers.set('location', url);
        if (process.env.BROWSER) {
            if (shouldWarnUnlocalizedUrl(url)) {
                this.context.request.console.warn(
                    `redirect is not localized:`, url);
            }
            let parsedURL = null;
            if (!url.startsWith('/')) {
                parsedURL = new URL(url);
            }
            if (parsedURL?.hostname &&
                    parsedURL.hostname !== this.context.request.hostname) {
                window.location.href = url;
            } else {
                this.debug('parsedURL', parsedURL);
                if (parsedURL) {
                    url = decodeURI(parsedURL.pathname) + parsedURL.search;
                } else {
                    url = url.toString();
                }
                this.debug('redirecing to', url);
                this.context.router.history.push(url);
            }
        }
    }
    linkToPack(id: string): string {
        return this.context.gtService.localizeUrlPath(`/packs/${id}`);
    }
    linkToCollection(id: string): string {
        return this.context.gtService.localizeUrlPath(`/collections/${id}`);
    }
    linkToView(gif: api.Post): string {
        // API will utf8 percent encoded urls, so we need to decode.
        const parsedURL = new URL(decodeURIComponent(gif.itemurl));
        return this.context.gtService.localizeUrlPath(
            decodeURI(parsedURL.pathname)) + parsedURL.search;
    }
    linkToProfile(user?: Partial<api.User>, mediatype?: string): string {
        if (!user || (!user.url && !user.username)) {
            user = this.context.authService.getUser();
        }
        const userPath = this.context.authService.userHasFlag('partner') ?
            '/official/' : '/users/';
        const mediaPath = (mediatype ? `/${mediatype}` : '');

        if (user?.url) {
            return this.context.gtService.localizeUrlPath(localURL(
                user.url.toLowerCase().replace('/user/', userPath) + mediaPath));
        }
        let username = '';
        if (user?.username) {
            username = user.username;
        }
        const path = `${userPath}${username.toLowerCase()}${mediaPath}`;
        return this.context.gtService.localizeUrlPath(path);
    }
    linkToSearch(terms: string|string[], mediatype?: string): string {
        if (!Array.isArray(terms)) {
            terms = terms.split(rootScope.searchInputSepRegEx);
        } else {
            terms = terms.concat();
        }
        terms = removeEmptyStrings(
            cleanTermsForURL(terms)).join(rootScope.searchURLSep);

        const url = `/search/${terms}${mediatype === 'stickers' ?
            rootScope.searchEndStickers :
            mediatype === 'memes' ?
                rootScope.searchEndMemes : rootScope.searchEndGifs}`;
        return this.context.gtService.localizeUrlPath(url);
    }
}


export class Page extends CustomComponent {
    static debugNamespace = 'Page';

    requiredScopes: string[];
    searchPageMultivariateGroup: string|null;
    noindex: boolean;
    h1_title: string;
    title: string;
    keywords: string;
    description: string;
    ttl: string;
    permissionText: string;
    canonicalRedirectEnabled: boolean;
    canonicalCaseSensitive: boolean;
    _canonicalURL?: string;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        emitter.emit('start-progress-bar');
        this.requiredScopes = [];
        this.searchPageMultivariateGroup = null;
        this.noindex = false;
        this.h1_title = '';
        this.title = '';
        this.keywords = '';
        this.description = '';
        this.ttl = this.context.CONSTANTS.PAGE_TTL;
        this.permissionText = '';
        this.handleNewPage(props, this.state);
    }
    componentDidMount() {
        // FIXME: pages that depend on auth data should subscribe to api.auth.
        emitter.on('authDataSet', this.triggerUpdate);
    }
    componentWillUnmount() {
        emitter.off('authDataSet', this.triggerUpdate);
    }
    componentWillUpdate(nextProps: jsx.Props, state: jsx.State) {
        // this.debug('componentWillUpdate', nextProps, state);
        // React/Inferno will reuse components when switching between pages of
        // the same type.
        // NOTE: componentWillUpdate gets called on state changes as well.
        this.debug('componentWillUpdate');
        if (this.pageChanged(nextProps, state)) {
            this.handleNewPage(nextProps, state);
        } else if (this.propsNeedProcessing(nextProps, state)) {
            this.processNewProps(nextProps, state);
        }
    }
    handleNewPage(props: jsx.Props, state: jsx.State) {
        this.context.loggedTimer.time('Custom App', 'pageDone', 'page_start');
        if (process.env.BROWSER) {
            // Only need to worry about this client side. On the server side,
            // we need to keep any data set before now.
            this.context.response.reset();
        }
        this.canonicalRedirectEnabled = true;
        this.canonicalCaseSensitive = true;
        this.pageInit(props, state);
        this.debug('handleNewPage');
        this.processNewProps(props, state);
        Promise.all(
            this.context.store.pendingPromises).then(this.handlePageDone);
    }
    pageInit(props: jsx.Props, state: jsx.State) {
        throw new Error('pageInit missing');
    }
    processNewProps(props: jsx.Props, state: jsx.State) {

    }
    pageChanged(props: jsx.Props, state: jsx.State) {
        this.debug('pageChanged', props, state);
        // According to the docs, shouldComponentUpdate may be treated as a
        // hint in the future, which is why we take manual control over the
        // process here. This determines whether `this.pageInit()` gets called,
        // which is where page setup code should go, instead of in `render()`,
        // which could result in state being overwritten.
        return false;
    }
    propsNeedProcessing(props: jsx.Props, state: jsx.State) {
        this.debug('propsNeedProcessing', props, state);
        // According to the docs, shouldComponentUpdate may be treated as a
        // hint in the future, which is why we take manual control over the
        // process here. This determines whether `this.processNewProps()` gets
        // called, which is where changes in props and state within a page load
        // should be handled, instead of in `render()`, which could result in
        // state being overwritten.
        return false;
    }
    get currentLocation() {
        return this.context.router.history.location;
    }
    get currentPath() {
        return this.context.router.history.location.pathname;
    }
    get canonicalURL() {
        if (this._canonicalURL) {
            return this._canonicalURL;
        }
        const gtService = this.context.gtService;
        let currentPath = this.currentPath;
        // In order to support locales with country codes, we'll need to allow
        // capital letters in the country code.
        if (this.canonicalCaseSensitive) {
            let i18n = false;
            const localePrefix = `/${gtService.getLocaleUrlPrefix()}/`;
            if (currentPath.startsWith(localePrefix)) {
                i18n = true;
                currentPath = currentPath.split(localePrefix)[1];
            }
            currentPath = currentPath.toLowerCase();
            if (i18n) {
                currentPath = `${localePrefix}${currentPath}`;
            }
        }
        return this.fullURL(currentPath);
    }
    get encodedCanonicalURL() {
        return encodeURIComponent(this.canonicalURL);
    }
    setCanonicalURL(url: string) {
        if (url.charAt(0) === '/') {
            url = this.fullURL(url);
        }
        this._canonicalURL = url;
        this.debug('setCanonicalURL', this._canonicalURL);
    }
    @autobind
    handlePageDone() {
        // This logging statement is needed for our e2e tests. :(
        testLogging('handlePageDone');
        emitter.emit('finish-progress-bar');
        // Facebook and Google Analytics tracking
        if (process.env.BROWSER && window.ga) {
            let pageUrl = this.context.router.history.location.pathname;
            if (this.context.router.history.location.search) {
                pageUrl += this.context.router.history.location.search;
            }

            // These seem to already be set for gtag events.
            window.ga('set', {
                page: pageUrl,
                title: this.title,
            });
            window.setGaSessionDimension({
                // dimension1 is a custom dimension. If not set, we need to
                // clear it (null) to prevent previous values from being kept.
                dimension1: this.searchPageMultivariateGroup,
            });

            window.gtag('event', 'page_view');


            window.fbq('track', 'PageView');

            this.context.loggedTimer.timeEnd('Custom App', 'pageDone', 'page_done');
        }
    }
    /**
     * The page to render.
     * @returns {Element} JSX to render
     */
    renderPage(): Element {
        return <div/>;
    }
    /**
     * The page to render when an error is encountered.
     * @returns {Element} JSX to render
     */
    renderErrorPage(): Element {
        const CONSTANTS = this.context.CONSTANTS;
        const gettextSub = this.context.gtService.gettextSub;

        let msg;
        if (this.context.response.status === 404) {
            msg = gettextSub('We could not find the page you were looking for.');
            this.ttl = CONSTANTS.HTTP404_PAGE_TTL;
        } else if (this.context.response.status === 403) {
            msg = gettextSub('You do not have access to this page.');
            this.ttl = CONSTANTS.PAGE_TTL;
        } else {
            msg = gettextSub('An error occurred, please try again later.');
            this.ttl = CONSTANTS.PAGE_TTL;
        }

        return (
            <div className="error-page container page">
                <Metadata page={this}/>
                <div className="center-container heading-wrapper">
                    <h1>{ this.title }</h1>
                </div>
                <div className="single-view-container">
                    <p>{ msg }</p>
                </div>
            </div>
        );
    }
    render() {
        const CONSTANTS = this.context.CONSTANTS;
        const gettextSub = this.context.gtService.gettextSub;

        if (!process.env.BROWSER && this.context.request.preRendering) {
            return null;
        }
        let showErrorPage = false;
        let html;

        if (this.context.response.status >= 400 &&
                !this.context.response.keepPage) {
            this.title = gettextSub('{statusCode} Error', {
                statusCode: this.context.response.status,
            });
            showErrorPage = true;
        }

        // If new controller has a canonicalURL, redirect to it if necessary.
        // We have to compare the decoded versions since sometimes we need to
        // match upper case hex with lowercase hex, but otherwise still do a
        // case sensitive comparison.
        const canonicalPath = localURL(this.canonicalURL);
        const currentPath = this.currentPath;
        const request = this.context.request;
        this.debug('render:comparing urls (canonical:actual): ', [
            [
                CONSTANTS.NGINX_SCHEME,
                CONSTANTS.NGINX_HOST,
                canonicalPath,
            ],
            [
                request.protocol,
                request.hostname,
                currentPath,
            ],
        ]);

        const unencodedPathsMatch = canonicalPath === currentPath;
        if (!process.env.BROWSER && this.canonicalRedirectEnabled &&
                !this.context.response.headers.has('location') &&
                (!unencodedPathsMatch ||
                    CONSTANTS.NGINX_SCHEME !== request.protocol ||
                    CONSTANTS.NGINX_HOST !== request.hostname)) {
            this.context.request.console.info(
                'render redirect', currentPath, canonicalPath);
            this.context.response.headers.set('x-debug-canonicalPath',
                encodeHeaderVal(canonicalPath));
            this.context.response.headers.set('x-debug-currentPath',
                encodeHeaderVal(currentPath));
            this.context.response.headers.set('x-debug-scheme',
                CONSTANTS.NGINX_SCHEME);
            this.context.response.headers.set('x-debug-protocol',
                request.protocol);
            this.context.response.headers.set('x-debug-host',
                CONSTANTS.NGINX_HOST);
            this.context.response.headers.set('x-debug-hostname',
                request.hostname);
            this.redirect(
                this.canonicalURL + this.context.router.history.location.search,
                true,
            );
            html = <div/>;
        } else if (showErrorPage) {
            this.debug('render renderErrorPage');
            html = this.renderErrorPage();
        } else {
            this.debug('render renderPage');
            html = this.renderPage();
        }
        this.debug('render:scope', 'checking');
        for (const scope of this.requiredScopes) {
            if (!this.context.authService.hasOAuthScope(scope)) {
                this.debug('render:scope', 'missing', scope);
                this.context.authService.showScopeRefreshDialog({
                    permissionText: this.permissionText,
                });
                break;
            }
        }
        // TODO: move metadata generation here/getMetadata method.

        let ttl = this.ttl || CONSTANTS.PAGE_TTL;
        const DAY = 86400;
        let swr = DAY * 3;
        if (this.noindex) {
            // If not indexed, it's less important to always have a cached
            // version ready.
            ttl = CONSTANTS.PAGE_TTL;
            swr = DAY * 2;
        }
        this.context.response.headers.set('cache-control',
            `max-age=${ttl}, stale-while-revalidate=${swr}`);
        return html;
    }
}
